import { useCallback, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { useGetOrdersByDateRange } from "./useGetOrdersByDateRange";
import StatusMaintenanceOrders from "../status/StatusMaintenanceOrders";
import { useGetUserDetails } from "../authentication/useGetUserDetails";

/* =========================
   Layout primitives
   ========================= */

const Content = styled.div`
  height: auto;
  width: 100%;
  padding: 1.4rem;
  overflow-y: auto;
  box-sizing: border-box;
`;

const StyledHeading = styled.h5`
  color: var(--color-grey-500);
  padding: 1rem;
`;

const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const MonthSection = styled.section`
  margin-bottom: 2rem;
`;

const MonthHeader = styled.h3`
  margin: 0.8 rem 0;
  color: var(--color-grey-800);
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(25rem, 1fr));
  gap: 1.2rem;
`;

/* =========================
   Date helpers
   ========================= */

function getMonthKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}`;
}

function getMonthLabel(date) {
  return date.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
}

/* =========================
   Grouping logic
   ========================= */

function groupOrdersByMonth(orders) {
  const result = {};

  orders.forEach(order => {
    const date = new Date(order.plannedDate);
    const monthKey = getMonthKey(date);

    if (!result[monthKey]) {
      result[monthKey] = {
        label: getMonthLabel(date),
        orders: [],
      };
    }

    result[monthKey].orders.push(order);
  });

  // Stable chronological order inside each month
  Object.values(result).forEach(month => {
    month.orders.sort(
      (a, b) => new Date(a.plannedDate) - new Date(b.plannedDate)
    );
  });

  return result;
}

/* =========================
   Component
   ========================= */

function OrdersByMonth({ fromDate, toDate, asset, resource }) {
  const { data, isLoading, isError, error } =
    useGetOrdersByDateRange(fromDate, toDate, {
      asset,
      resource,
    });

const userImageRef = useRef({});
const { mutate: fetchUserDetails } = useGetUserDetails();

const fetchUserImage = useCallback(
  (name) =>
    new Promise((resolve) => {
      fetchUserDetails(
        { wiToolName: name },
        {
          onSuccess: (response) => {
            const img = response?.data?.data?.userImageBase64;
            if (response?.userExists && img?.trim()) {
              userImageRef.current[name] = img;
            }
            resolve();
          },
          onError: () => resolve(),
        }
      );
    }),
  [fetchUserDetails]
);






  const grouped = useMemo(() => {
    if (!data?.data?.length) return null;
    return groupOrdersByMonth(data.data);
  }, [data]);

  useEffect(() => {
  if (!grouped) return;

  async function preloadImages() {
    const uniqueNames = new Set();

    Object.values(grouped).forEach((month) => {
      month.orders.forEach((order) => {
        order.assignedResources
          ?.split("•")
          .map((n) => n.trim())
          .filter(Boolean)
          .forEach((name) => {
            if (!(name in userImageRef.current)) {
              uniqueNames.add(name);
            }
          });
      });
    });

    for (const name of uniqueNames) {
      await fetchUserImage(name);
    }
  }

  preloadImages();
}, [grouped, fetchUserImage]);

  if (isLoading) return <p>Loading…</p>;
  if (isError) return <p>Error: {error.message}</p>;
  if (!grouped) return <p>No orders found.</p>;

  return (
    <Content>
    <StyledHeading>Selected Orders</StyledHeading>
      <VerticalContainer>
        {Object.entries(grouped)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([monthKey, month]) => (
            <MonthSection key={monthKey}>
              <MonthHeader>{month.label}</MonthHeader>

              <OrdersGrid>
                {month.orders.map(order => (
                  <StatusMaintenanceOrders
                  key={order.id}
                  item={order}
                  userImages={userImageRef.current} />
                ))}
              </OrdersGrid>
            </MonthSection>
          ))}
      </VerticalContainer>
    </Content>
  );
}

export default OrdersByMonth;